<script lang="ts">
export const fileInputCls = cva("file-input", {
	variants: {
		color: {
			default: null,
			neutral: "file-input-neutral",
			primary: "file-input-primary",
			secondary: "file-input-secondary",
			accent: "file-input-accent",
			info: "file-input-info",
			success: "file-input-success",
			warning: "file-input-warning",
			error: "file-input-error",
		},
		size: {
			xs: "file-input-xs",
			sm: "file-input-sm",
			md: "file-input-md",
			lg: "file-input-lg",
		},
		variant: {
			default: null,
			ghost: "file-input-ghost",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type variant = VariantProps<typeof fileInputCls>;

export interface FileInputProps extends ClassProps {
	color?: variant["color"];
	variant?: variant["variant"];
	size?: variant["size"];
	accept?: string;
}

interface Props
	extends FileInputProps,
		/* @vue-ignore */ Omit<InputHTMLAttributes, keyof FileInputProps> {}
</script>

<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { useForwardExpose } from "reka-ui";
import { useAttrs, type InputHTMLAttributes } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { htmlAttrs } from "../../lib/utils";

const props = defineProps<Props>() as FileInputProps;
const attrs = useAttrs() as InputHTMLAttributes;

const modelValue = defineModel<File>();

function inputHandler(event: Event) {
	const target = event.target as HTMLInputElement | null;
	if (!target) {
		return;
	}

	const file = target.files?.[0];
	if (file != null) {
		modelValue.value = file;
	}
}

const { forwardRef } = useForwardExpose();
</script>

<template>
	<input
		:ref="forwardRef"
		:class="fileInputCls(props)"
		:accept="accept"
		type="file"
		v-bind="htmlAttrs(attrs)"
		@input="inputHandler"
	/>
</template>
